import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { ClinicalFormStep } from './ClinicalFormStep';
import { ClinicalElasticsStep } from './ClinicalElasticsStep';
import { ClinicalAttachmentStep } from './ClinicalAttachmentStep';
import { ClinicalVirtualAppointmentsStep } from './ClinicalVirtualAppointmentsStep';
import { ClinicalReviewStep } from './ClinicalReviewStep';
import { InstructionsStep } from '../InstructionsStep';
import { ConfirmDiscardModal } from '../ConfirmDiscardModal';
import { useClinicalDraftLifecycle } from './useClinicalDraftLifecycle';
import { useVirtualAppointmentPlans } from './useVirtualAppointmentPlans';
import { useVirtualAppointmentHistory } from './useVirtualAppointmentHistory';
import { hasOrthopulse } from '@odo/shared-ui/clinical/orthopulse';
import { resolveDocImagesInBackground } from '../../../../utils/docImageResolution';
import {
    collectDocImageFileIds,
    applyResolvedUrlsToDoc,
    tiptapDocToPlainText
} from '@odo/shared-ui';
import { EMPTY_TIPTAP_DOC } from '../dx-report/templates';
import {
    DEFAULT_ALIGNER_FIELDS,
    anyAlignerFieldEnabled
} from '@odo/shared-ui/clinical/alignerFields';
import { isClinicalFormValid, buildSteps } from './clinicalSteps';
import type { StepId } from './clinicalSteps';
import { WizardStepTabs } from './WizardStepTabs';
import { WizardFooter } from './WizardFooter';
import { formDataToFields } from './clinicalEventFields';
import {
    DEFAULT_TOGGLES,
    extractManufacturerPdfRef,
    extractTogglesFromEvent
} from '@odo/shared-ui/clinical/eventReaders';
import type { ManufacturerPdfRef } from '@odo/shared-ui/clinical/types';
import { uploadManufacturerAttachment } from '../../../../services/clinicalDrafts';
import type { UploadManufacturerAttachmentOptions } from '../../../../services/clinicalDrafts';
import type {
    ClinicalFormData,
    ClinicalWizardConfig,
    ClinicalDraftBody
} from './clinicalWizardConfig';
import type { ClinicalFormFields } from './ClinicalFormStep';
import type { ElasticScheme, LegacyElastics } from '@odo/shared-ui/clinical/elastics/model';
import {
    hasLegacyElastics,
    schemesBoundsErrorMessage,
    schemesHaveElastics,
    schemesRangeErrorMessage,
    schemesWithValidBounds
} from '@odo/shared-ui/clinical/elastics/persistence';
import type { SectionToggles } from '@odo/shared-ui/clinical/types';
import type { ActionRunnerProps, TimelineEvent } from '../types';
import type { TiptapDoc } from '@odo/shared-ui';

// Steps that fill the body edge to edge and scroll internally; the rest scroll inside a padded box.
const FULL_HEIGHT_STEPS: ReadonlySet<StepId> = new Set<StepId>([
    'instructions',
    'virtualAppointments'
]);

export interface ClinicalWizardProps<T extends ClinicalFormData> extends ActionRunnerProps {
    config: ClinicalWizardConfig<T>;
    editingEvent?: TimelineEvent;
    resumingDraft?: TimelineEvent;
    onSubmitted?: () => void;
    onRegisterBeforeClose?: (handler: (() => Promise<void>) | null) => void;
    /** When true, the wizard skips its own success screen and holds the
     *  submitting spinner until the parent (TaskSurface) unmounts it. */
    inWorkflow?: boolean;
}

function ClinicalWizard<T extends ClinicalFormData>({
    patientId,
    patientName,
    orthopulseStatus,
    onBack,
    onDataChange,
    config,
    editingEvent,
    resumingDraft,
    onSubmitted,
    onRegisterBeforeClose,
    inWorkflow = false
}: ClinicalWizardProps<T>) {
    const [stepIndex, setStepIndex] = useState(0);

    const steps = useMemo(() => buildSteps(), []);

    const defaults = useMemo(
        () => config.createDefaults(patientName, orthopulseStatus),
        [config, patientName, orthopulseStatus]
    );

    const initialFormData = useMemo<T>(() => {
        if (resumingDraft) {
            const extracted = config.extractFormDataFromEvent(resumingDraft);
            if (extracted) return extracted;
            const partial = (resumingDraft.payload as Record<string, unknown>).formData;
            if (partial && typeof partial === 'object') {
                return config.mergeWithDefaults(partial as Partial<T>, defaults);
            }
            return defaults;
        }
        if (editingEvent) {
            const extracted = config.extractFormDataFromEvent(editingEvent);
            // An edit is a new version of the document, dated today - not the
            // original's date (still adjustable on the form). Drafts keep theirs.
            if (extracted) return { ...extracted, date: defaults.date };
        }
        return defaults;
    }, [resumingDraft, editingEvent, defaults, config]);

    const [formData, setFormData] = useState<T>(() => initialFormData);
    const [attachment, setAttachment] = useState<File | null>(null);

    // Persisted upload ref — present after a successful upload or after resume.
    const initialAttachmentRef = useMemo<ManufacturerPdfRef | null>(() => {
        if (resumingDraft) return extractManufacturerPdfRef(resumingDraft);
        if (editingEvent) return extractManufacturerPdfRef(editingEvent);
        return null;
    }, [resumingDraft, editingEvent]);

    const [attachmentRef, setAttachmentRef] = useState<ManufacturerPdfRef | null>(
        () => initialAttachmentRef
    );
    const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
    const [attachmentUploadError, setAttachmentUploadError] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<number | null>(null);

    const initialNotesDoc = useMemo<TiptapDoc>(
        () => initialFormData.notesDoc ?? EMPTY_TIPTAP_DOC,
        [initialFormData]
    );

    const [notesDoc, setNotesDoc] = useState<TiptapDoc>(() => initialNotesDoc);

    useEffect(() => {
        if (!(resumingDraft ?? editingEvent)) return undefined;
        return resolveDocImagesInBackground(
            patientId,
            [initialNotesDoc],
            'ClinicalWizard.resolveNoteImageUrls',
            (urls) => setNotesDoc((prev) => applyResolvedUrlsToDoc(prev, urls))
        );
    }, [resumingDraft, editingEvent, patientId, initialNotesDoc]);

    const initialToggles = useMemo(() => {
        if (resumingDraft) return extractTogglesFromEvent(resumingDraft);
        if (editingEvent) return extractTogglesFromEvent(editingEvent);
        return DEFAULT_TOGGLES;
    }, [resumingDraft, editingEvent]);

    const [toggles, setToggles] = useState<SectionToggles>(() => initialToggles);

    const planner = useVirtualAppointmentPlans({
        patientId,
        editingEvent,
        resumingDraft,
        series: config.virtualAppointmentTriggerSeries
    });
    const historyVas = useVirtualAppointmentHistory({
        patientId,
        keepGroupId: editingEvent?.groupId ?? resumingDraft?.groupId ?? null,
        enabled: Boolean(config.cancelsEarlierVirtualAppointments)
    });
    const vaHistory = useMemo(
        () => (historyVas ? { vas: historyVas, cancelsOnSubmit: !editingEvent } : undefined),
        [historyVas, editingEvent]
    );
    const {
        submitFields: vaSubmitFields,
        onSubmitted: onVaSubmitted,
        submitBlockFor: vaSubmitBlockFor
    } = planner;

    const [rebuildKey, setRebuildKey] = useState(0);
    const [mergedPdf, setMergedPdf] = useState<Blob | null>(null);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(false);
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

    const currentStep = steps[stepIndex];

    const isDirty = useMemo(() => {
        // A changed attachmentRef (attach or remove) counts as dirty so auto-save persists it.
        const refChanged = JSON.stringify(attachmentRef) !== JSON.stringify(initialAttachmentRef);
        if (refChanged) return true;
        if (attachment !== null) return true;
        if (planner.isDirty) return true;
        if (toggles.elasticsEnabled !== initialToggles.elasticsEnabled) return true;
        if (toggles.alignerScheduleEnabled !== initialToggles.alignerScheduleEnabled) return true;
        if (JSON.stringify(toggles.alignerFields) !== JSON.stringify(initialToggles.alignerFields))
            return true;
        if (JSON.stringify(notesDoc) !== JSON.stringify(initialNotesDoc)) return true;
        const formDataWithoutDoc = { ...formData, notesDoc: undefined };
        const initialWithoutDoc = { ...initialFormData, notesDoc: undefined };
        return JSON.stringify(formDataWithoutDoc) !== JSON.stringify(initialWithoutDoc);
    }, [
        formData,
        initialFormData,
        attachment,
        attachmentRef,
        initialAttachmentRef,
        planner.isDirty,
        toggles,
        initialToggles,
        notesDoc,
        initialNotesDoc
    ]);

    // The draft routes validate aligner bounds too, whether or not the section is enabled.
    const elasticsBoundsError = useMemo(
        () => schemesBoundsErrorMessage(config.getElasticsSchemes(formData)),
        [config, formData]
    );
    const elasticsRangeError = useMemo(
        () => schemesRangeErrorMessage(config.getElasticsSchemes(formData)),
        [config, formData]
    );

    const buildDraftBody = useCallback((): ClinicalDraftBody<T> => {
        const imageFileIds = collectDocImageFileIds(notesDoc);
        const alignerFields = toggles.alignerFields ?? DEFAULT_ALIGNER_FIELDS;
        const body: ClinicalDraftBody<T> = {
            formData: { ...formData, notesDoc } as Partial<T>,
            ...planner.draftFields,
            elasticsEnabled: toggles.elasticsEnabled,
            alignerScheduleEnabled: anyAlignerFieldEnabled(alignerFields),
            alignerFields,
            ...(imageFileIds.length > 0 ? { imageFileIds } : {}),
            manufacturerPdf: attachmentRef ?? null
        };
        if (editingEvent) {
            body.groupId = editingEvent.groupId;
        } else if (resumingDraft?.groupId) {
            body.groupId = resumingDraft.groupId;
        }
        return body;
    }, [
        formData,
        notesDoc,
        planner.draftFields,
        toggles,
        editingEvent,
        resumingDraft,
        attachmentRef
    ]);

    const {
        currentDraftId,
        currentDraftIdRef,
        isSavingDraft,
        isSavingDraftSilent,
        showSavedBadge,
        setCurrentDraftId,
        handleSaveDraft,
        waitForPendingDraftSave
    } = useClinicalDraftLifecycle({
        patientId,
        resumingDraft,
        drafts: config.drafts,
        buildDraftBody,
        isDirty,
        stepIndex,
        isOnReviewStep: currentStep.id === 'review',
        isSubmitting,
        saveBlockedReason: elasticsBoundsError,
        onDataChange,
        onRegisterBeforeClose
    });

    // A successful submit clears the draft id; don't flag it as vanished during the hand-off.
    const vaBlockedHint = isSubmitting
        ? planner.submitBlockedHint
        : vaSubmitBlockFor(currentDraftId);

    const handleRequestExit = useCallback(() => {
        if (isDirty || currentDraftId) {
            setShowDiscardConfirm(true);
        } else {
            onBack();
        }
    }, [isDirty, currentDraftId, onBack]);

    const handleDiscard = useCallback(async () => {
        setShowDiscardConfirm(false);
        const draftId = currentDraftIdRef.current;
        if (draftId) {
            try {
                await config.drafts.deleteDraft(patientId, draftId);
                onDataChange?.();
            } catch {
                // Best-effort delete; still navigate away
            }
        }
        onBack();
    }, [currentDraftIdRef, patientId, onBack, onDataChange, config.drafts]);

    const handleSaveDraftAndExit = useCallback(async () => {
        try {
            await handleSaveDraft();
        } catch {
            return;
        }
        setShowDiscardConfirm(false);
        onBack();
    }, [handleSaveDraft, onBack]);

    const handleCancelDiscard = useCallback(() => {
        setShowDiscardConfirm(false);
    }, []);

    const elasticsValid = useMemo(() => {
        if (!toggles.elasticsEnabled) return true;
        const schemes = config.getElasticsSchemes(formData);
        if (schemesHaveElastics(schemes)) {
            // Every scheme needs a valid (fully-entered) aligner range, and every
            // placed elastic a size.
            return (
                !elasticsRangeError &&
                schemes.every((s) => s.elastics.every((p) => p.size.trim().length > 0))
            );
        }
        // No placed elastics — a legacy record's Left/Right values still count as
        // "elastics added"; a brand-new record with nothing placed does not.
        return hasLegacyElastics(formData.legacyElastics);
    }, [toggles.elasticsEnabled, config, formData, elasticsRangeError]);

    // Why Submit is disabled; elastics never gates navigation between steps.
    const elasticsInvalidHint = useMemo(() => {
        if (elasticsValid) return undefined;
        if (!schemesHaveElastics(config.getElasticsSchemes(formData)))
            return 'Add at least one elastic, or turn the section off';
        return elasticsRangeError ?? 'Every placed elastic needs a size';
    }, [elasticsValid, config, formData, elasticsRangeError]);

    const stepValidity = useMemo<Record<StepId, boolean>>(
        () => ({
            form: isClinicalFormValid(formData.date, formData.patientName),
            // Elastics gates Submit only (see submitDisabled), never navigation.
            elastics: true,
            instructions: true,
            attachment: true,
            virtualAppointments: true,
            review: mergedPdf !== null
        }),
        [formData.date, formData.patientName, mergedPdf]
    );

    // Blocked while the manufacturer attachment is uploading or has failed with
    // a file still present. The user must wait for success, retry, or remove it.
    const isAttachmentBlocked =
        isUploadingAttachment || (attachment !== null && attachmentUploadError !== null);

    const attachmentBlockedHint = isAttachmentBlocked
        ? isUploadingAttachment
            ? 'Waiting for attachment upload…'
            : 'Attachment upload failed — retry or remove it'
        : undefined;

    const canAdvance = stepValidity[currentStep.id] && !isAttachmentBlocked;

    const handleFieldChange = useCallback((field: keyof ClinicalFormFields, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    }, []);

    const handleNextAppointmentChange = useCallback(
        (value: string) => {
            setFormData((prev) => config.setNextAppointment(prev, value));
        },
        [config]
    );

    const handleElasticsSchemesChange = useCallback(
        (next: ElasticScheme[]) => {
            setFormData((prev) => config.setElasticsSchemes(prev, next));
        },
        [config]
    );

    const handleLegacyElasticsChange = useCallback((next: LegacyElastics) => {
        setFormData((prev) => ({ ...prev, legacyElastics: next }));
    }, []);

    const handleInstructionsChange = useCallback((doc: TiptapDoc) => {
        setNotesDoc(doc);
        const plainText = tiptapDocToPlainText(doc);
        setFormData((prev) => ({ ...prev, notes: plainText, notesDoc: doc }));
    }, []);

    // Track the currently-pending upload so we can cancel it on component unmount.
    const uploadAbortRef = useRef<{ cancelled: boolean; abort: (() => void) | null }>({
        cancelled: false,
        abort: null
    });

    const triggerUpload = useCallback(
        async (file: File) => {
            const kind = config.kindLabel.toLowerCase() as 'sdi' | 'rdi';
            setIsUploadingAttachment(true);
            setAttachmentUploadError(null);
            setUploadProgress(null);
            const guard: { cancelled: boolean; abort: (() => void) | null } = {
                cancelled: false,
                abort: null
            };
            uploadAbortRef.current = guard;

            const opts: UploadManufacturerAttachmentOptions = {
                onProgress: (fraction) => {
                    if (!guard.cancelled) setUploadProgress(fraction);
                },
                onRegisterAbort: (abortFn) => {
                    guard.abort = abortFn;
                    uploadAbortRef.current.abort = abortFn;
                }
            };

            try {
                const ref = await uploadManufacturerAttachment(patientId, kind, file, opts);
                if (guard.cancelled) return;
                setAttachmentRef(ref);
                setUploadProgress(null);
            } catch (err: unknown) {
                if (guard.cancelled) return;
                const msg = err instanceof Error ? err.message : 'Upload failed';
                setAttachmentUploadError(msg);
                setUploadProgress(null);
            } finally {
                if (!guard.cancelled) setIsUploadingAttachment(false);
            }
        },
        [patientId, config.kindLabel]
    );

    useEffect(() => {
        return () => {
            uploadAbortRef.current.cancelled = true;
            uploadAbortRef.current.abort?.();
        };
    }, []);

    const handleAttachmentChange = useCallback(
        (file: File | null) => {
            setAttachment(file);
            if (file === null) {
                // Clear both ref and any upload state when user removes the attachment.
                setAttachmentRef(null);
                setAttachmentUploadError(null);
                setIsUploadingAttachment(false);
                setUploadProgress(null);
                uploadAbortRef.current.cancelled = true;
                uploadAbortRef.current.abort?.();
            } else {
                // Reset ref so the new upload replaces a stale one.
                setAttachmentRef(null);
                void triggerUpload(file);
            }
        },
        [triggerUpload]
    );

    const handleAttachmentRetry = useCallback(() => {
        if (attachment) void triggerUpload(attachment);
    }, [attachment, triggerUpload]);

    // Trigger a single PDF rebuild whenever the user lands on the review step.
    useEffect(() => {
        if (currentStep.id === 'review') setRebuildKey((k) => k + 1);
    }, [currentStep.id]);

    const handleTogglesChange = useCallback((next: SectionToggles) => {
        setToggles(next);
    }, []);

    const handleMergedPdfReady = useCallback((blob: Blob) => {
        setMergedPdf(blob);
    }, []);

    const handleNext = useCallback(() => {
        setStepIndex((i) => Math.min(i + 1, steps.length - 1));
    }, [steps.length]);

    const handleBack = useCallback(() => {
        setStepIndex((i) => Math.max(i - 1, 0));
    }, []);

    // buildPdfForReview wraps config.buildPdf and injects the cached attachmentBytes
    // parameter. The review step calls this with the bytes it fetched (or null).
    const buildPdfForReview = useCallback(
        (attachmentBytes: Uint8Array | null) =>
            config.buildPdf(
                formData,
                attachment,
                hasOrthopulse(orthopulseStatus),
                toggles,
                attachmentBytes
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rebuildKey]
    );

    const handleSubmit = useCallback(async () => {
        const vaFields = vaSubmitFields;
        if (!mergedPdf || !vaFields) return;
        setIsSubmitting(true);
        setSubmitError(null);
        // Track whether we handed off to the workflow — used to keep the spinner
        // alive until TaskSurface unmounts us (instead of flashing the success screen).
        let handedOffToWorkflow = false;
        try {
            // An auto-save still creating the draft would otherwise leave an orphan copy.
            await waitForPendingDraftSave();
            const blocked = vaSubmitBlockFor(currentDraftIdRef.current);
            if (blocked) {
                setSubmitError(blocked);
                return;
            }
            const draftId = currentDraftIdRef.current ?? undefined;
            const withNotes: T = { ...formData, notesDoc };
            // With the section off its step is hidden, so drop schemes the backend would reject.
            const submittableFormData = toggles.elasticsEnabled
                ? withNotes
                : config.setElasticsSchemes(
                      withNotes,
                      schemesWithValidBounds(config.getElasticsSchemes(formData))
                  );
            if (editingEvent) {
                await config.edit({
                    patientId,
                    patientName,
                    eventId: editingEvent.id,
                    formData: submittableFormData,
                    ...vaFields,
                    mergedPdf,
                    toggles,
                    draftId,
                    manufacturerPdfRef: attachmentRef
                });
            } else {
                await config.submit({
                    patientId,
                    patientName,
                    formData: submittableFormData,
                    ...vaFields,
                    mergedPdf,
                    toggles,
                    draftId,
                    manufacturerPdfRef: attachmentRef
                });
            }
            setCurrentDraftId(null);
            onVaSubmitted();
            onDataChange?.();
            if (inWorkflow) {
                // TaskSurface's completion card is the success state; keep the spinner
                // up while the parent advances the workflow.
                handedOffToWorkflow = true;
                onSubmitted?.();
            } else {
                setSubmitted(true);
                onSubmitted?.();
            }
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            setSubmitError(`Failed to submit: ${message}`);
        } finally {
            if (!handedOffToWorkflow) {
                setIsSubmitting(false);
            }
        }
    }, [
        mergedPdf,
        formData,
        notesDoc,
        patientId,
        patientName,
        vaSubmitFields,
        vaSubmitBlockFor,
        onVaSubmitted,
        toggles,
        editingEvent,
        onDataChange,
        onSubmitted,
        config,
        currentDraftIdRef,
        waitForPendingDraftSave,
        setCurrentDraftId,
        attachmentRef,
        inWorkflow
    ]);

    const titleLabel = useMemo(() => {
        if (resumingDraft) {
            return editingEvent ? 'Resume edit draft' : 'Resume draft';
        }
        return editingEvent ? `Edit ${config.kindLabel}` : `New ${config.kindLabel}`;
    }, [resumingDraft, editingEvent, config.kindLabel]);

    if (submitted) {
        return (
            <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
                <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
                    <CheckCircleIcon className="w-6 h-6 text-green-500" />
                </div>
                <div className="text-center">
                    <div className="text-sm font-semibold text-gray-800">
                        {editingEvent
                            ? `${config.kindLabel} updated`
                            : `${config.kindLabel} submitted`}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{config.successCopy}</div>
                </div>
                <button
                    onClick={onBack}
                    className="mt-2 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    Back to {config.kindLabel}s
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col flex-1 min-h-0">
            <ConfirmDiscardModal
                isOpen={showDiscardConfirm}
                onCancel={handleCancelDiscard}
                onDiscard={handleDiscard}
                onSaveDraft={handleSaveDraftAndExit}
                isSaving={isSavingDraft}
                saveDisabled={isAttachmentBlocked}
                saveDisabledHint={attachmentBlockedHint}
            />

            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
                <button
                    onClick={handleRequestExit}
                    className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-sm"
                >
                    <ArrowLeftIcon className="w-3.5 h-3.5" />
                    Back to {config.kindLabel}s
                </button>
                <div className="text-sm font-semibold text-gray-800">{titleLabel}</div>
                <div className="w-[120px] flex items-center justify-end">
                    {isSavingDraftSilent ? (
                        <span className="flex items-center gap-1 text-[10px] text-gray-400">
                            <span className="w-2.5 h-2.5 rounded-full border border-gray-400 border-t-transparent animate-spin inline-block" />
                            Saving…
                        </span>
                    ) : showSavedBadge ? (
                        <span className="text-[10px] text-emerald-600 font-medium">✓ Saved</span>
                    ) : null}
                </div>
            </div>

            <WizardStepTabs
                steps={steps}
                stepIndex={stepIndex}
                onSelect={setStepIndex}
                isAttachmentBlocked={isAttachmentBlocked}
                attachmentBlockedHint={attachmentBlockedHint}
            />

            <div
                className={`flex-1 min-h-0 ${FULL_HEIGHT_STEPS.has(currentStep.id) ? 'flex flex-col overflow-hidden' : 'overflow-auto p-5'}`}
            >
                {currentStep.id === 'form' && (
                    <ClinicalFormStep
                        patientId={patientId}
                        fields={formDataToFields(formData)}
                        onFieldChange={handleFieldChange}
                        nextAppointment={config.getNextAppointment(formData)}
                        onNextAppointmentChange={handleNextAppointmentChange}
                        nextAppointmentLabel={config.nextAppointmentLabel}
                        nextAppointmentPlaceholder={config.nextAppointmentPlaceholder}
                        alignerWearOptions={config.alignerWearOptions}
                        orthopulseStatus={orthopulseStatus}
                        toggles={toggles}
                        onToggleChange={handleTogglesChange}
                    />
                )}
                {currentStep.id === 'elastics' && (
                    <ClinicalElasticsStep
                        schemes={config.getElasticsSchemes(formData)}
                        onChange={handleElasticsSchemesChange}
                        legacyElastics={formData.legacyElastics}
                        onLegacyChange={handleLegacyElasticsChange}
                        enabled={toggles.elasticsEnabled}
                        onToggle={() =>
                            handleTogglesChange({
                                ...toggles,
                                elasticsEnabled: !toggles.elasticsEnabled
                            })
                        }
                    />
                )}
                {currentStep.id === 'instructions' && (
                    <InstructionsStep
                        patientId={patientId}
                        value={notesDoc}
                        onChange={handleInstructionsChange}
                    />
                )}
                {currentStep.id === 'attachment' && (
                    <ClinicalAttachmentStep
                        attachment={attachment}
                        attachmentRef={attachmentRef}
                        isUploading={isUploadingAttachment}
                        uploadProgress={uploadProgress}
                        uploadError={attachmentUploadError}
                        onChange={handleAttachmentChange}
                        onRetry={handleAttachmentRetry}
                        kindLabel={config.kindLabel}
                    />
                )}
                {currentStep.id === 'virtualAppointments' && (
                    <ClinicalVirtualAppointmentsStep
                        patientId={patientId}
                        planner={planner}
                        history={vaHistory}
                    />
                )}
                {currentStep.id === 'review' && (
                    <ClinicalReviewStep
                        patientId={patientId}
                        kindLabel={config.kindLabel}
                        fields={formDataToFields(formData)}
                        elasticsSchemes={config.getElasticsSchemes(formData)}
                        legacyElastics={formData.legacyElastics}
                        nextAppointment={config.getNextAppointment(formData)}
                        notesDoc={notesDoc}
                        attachment={attachment}
                        attachmentRef={attachmentRef}
                        virtualAppointments={{
                            frozen: planner.frozen,
                            planned: planner.planned,
                            savedTriggers: planner.savedTriggers,
                            status: planner.loadStatus,
                            onRetry: planner.retryLoad
                        }}
                        rebuildKey={rebuildKey}
                        onMergedPdfReady={handleMergedPdfReady}
                        buildPdf={buildPdfForReview}
                    />
                )}
            </div>

            <WizardFooter
                isReviewStep={currentStep.id === 'review'}
                canGoBack={stepIndex !== 0}
                canAdvance={canAdvance}
                isSubmitting={isSubmitting}
                submitDisabled={
                    isSubmitting ||
                    mergedPdf === null ||
                    isAttachmentBlocked ||
                    !elasticsValid ||
                    vaBlockedHint !== undefined
                }
                isSavingDraft={isSavingDraft}
                isSavingDraftSilent={isSavingDraftSilent}
                isAttachmentBlocked={isAttachmentBlocked}
                attachmentBlockedHint={attachmentBlockedHint}
                submitBlockedHint={
                    currentStep.id === 'review' ? (elasticsInvalidHint ?? vaBlockedHint) : undefined
                }
                submitError={submitError}
                onBack={handleBack}
                // Failures are already toasted; catching keeps the rejection out of Sentry.
                onSaveDraft={() => void handleSaveDraft().catch(() => undefined)}
                onNext={handleNext}
                onSubmit={handleSubmit}
            />
        </div>
    );
}

export { ClinicalWizard };
